import Reveal from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { Wheat, HandHeart, Scale, Users } from "lucide-react";

const VALUES = [
  { icon: Wheat, title: "Pure Stock", hindi: "शुद्ध सामान", body: "Every grain, dal and spice is checked before it reaches the shelf." },
  { icon: Scale, title: "Fair Weighing", hindi: "सही तौल", body: "Honest scales, honest prices — the way a neighbourhood shop should be." },
  { icon: HandHeart, title: "Personal Care", hindi: "अपनापन", body: "We remember your usual order and keep your favourites aside." },
  { icon: Users, title: "Community First", hindi: "मोहल्ला पहले", body: "Credit for regulars, help in hard months — we grow with our neighbours." },
];

const About = () => (
  <main className="pt-32 pb-0">
    <section className="px-6 md:px-10 grain spotlight relative">
      <div className="max-w-4xl mx-auto text-center">
        <Reveal>
          <p className="uppercase text-xs sm:text-sm font-semibold tracking-[0.2em] text-spice">About Us</p>
          <h1 className="font-serif font-bold tracking-tight text-ink text-4xl sm:text-5xl lg:text-6xl mt-4">
            A small shop with a <span className="italic text-spice">very big</span> promise.
          </h1>
          <p className="font-hindi text-forest text-xl mt-6">हमारी कहानी — आपकी गलियों से</p>
        </Reveal>
      </div>

      <div className="max-w-3xl mx-auto mt-16 flex flex-col gap-8 text-bark leading-relaxed text-base md:text-lg pb-24">
        <Reveal>
          <p>
            Raj Kumari Kirana Store began as a single wooden counter and a sack of wheat atta. What has not changed in
            over three decades is the rule of the house: <em className="font-serif text-coffee">if we would not serve it
            at our own table, we will not sell it at ours.</em>
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <p>
            Every morning starts the same way — fresh stock from trusted mandis and mills, shelves wiped clean, the
            weighing scale checked twice. From everyday staples like rice, dal and atta, to hand-pounded masalas,
            cold-pressed oils, festive pooja samagri and your evening namkeen, the shop carries over two thousand
            items a household could need.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <p>
            For our regulars we keep monthly ration lists ready, offer home delivery in the neighbourhood, and never
            let a familiar face leave empty-handed. That is what <span className="font-hindi text-spice">आपकी अपनी दुकान</span> means
            to us — your own shop, in every sense.
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee />

    <section className="px-6 md:px-10 py-24">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <h2 className="font-serif font-bold tracking-tight text-cocoa text-3xl md:text-4xl text-center">
            What we stand for
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-14">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="bg-white border border-linen rounded-2xl p-8 h-full hover:shadow-lg hover:shadow-ink/5 hover:-translate-y-1 transition-all duration-500">
                <v.icon className="text-spice" size={30} />
                <h3 className="font-serif font-semibold text-coffee text-xl mt-5">{v.title}</h3>
                <p className="font-hindi text-spice text-sm mt-1">{v.hindi}</p>
                <p className="text-bark text-sm leading-relaxed mt-3">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-20 text-center">
          {[
            ["30+", "Years of Service"],
            ["2000+", "Items in Stock"],
            ["7", "Days Open a Week"],
            ["100%", "Neighbourhood Trust"],
          ].map(([num, label], i) => (
            <Reveal key={label} delay={i * 0.06}>
              <p className="font-serif font-bold text-4xl md:text-5xl text-spice">{num}</p>
              <p className="uppercase text-xs font-semibold tracking-[0.18em] text-mut mt-2">{label}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </main>
);

export default About;
